import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import LoadingState from '../components/LoadingState';
import { useUser } from '../contexts/UserContext';

interface InvitationResponse {
  success: boolean;
  client_id: number;
  message?: string;
}

const Welcome: React.FC = () => {
  const [invitationCode, setInvitationCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { reloadUser, setSelectedClientId } = useUser();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Accept the invitation
      const response = await apiService.post<InvitationResponse>('/api/auth/accept-invitation', { invitation_code: invitationCode });
      if (!response.data || !response.data.client_id) {
        throw new Error('Invalid response from server');
      }

      // Reload user data to get the new client assignment
      await reloadUser();

      // Set the selected client ID
      setSelectedClientId(response.data.client_id);

      // Navigate to dashboard
      navigate('/app');
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      setError(error.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingState variant="page" size="medium" message="Processing invitation..." />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 8rem)',
        p: 3
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 6,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          borderRadius: 2
        }}
      >
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom
          sx={{
            fontWeight: 600,
            mb: 2,
            fontSize: { xs: '1.75rem', sm: '2.5rem' },
            color: 'primary.main'
          }}
        >
          Travel Minds
        </Typography>
        <Typography 
          variant="h4" 
          component="h2" 
          gutterBottom 
          sx={{ 
            fontWeight: 500,
            mb: 6,
            fontSize: { xs: '1.25rem', sm: '1.75rem' },
            color: 'text.secondary',
            letterSpacing: '0.5px'
          }}
        >
          Marketing Portal
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          This is an exclusive system for participating members. To access the portal, you'll need a valid invitation code provided by your agency or Travel Minds support.
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          If you haven't received an invitation code, please contact support@travelminds.com to request access.
        </Typography>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Enter Invitation Code"
            value={invitationCode}
            onChange={(e) => setInvitationCode(e.target.value)}
            error={!!error}
            helperText={error}
            margin="normal"
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              }
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isLoading}
            sx={{ 
              mt: 3,
              py: 1.5,
              borderRadius: 1.5,
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            Access Portal
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default Welcome; 